import { useEffect, useMemo, useRef } from "react";
import { AlertTriangle, ImageIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useTranslation } from "react-i18next";
import { optionsPosition } from "@/lib/image";
import { translateImageLinkRule } from "@/lib/import/image-link-i18n";
import { ImageLinkItem } from "@/api/image-link";
import { ImportTable } from "@/lib/import/import-types";

/**
 * Validation des rapprochements approximatifs, un par un.
 *
 * Meme grammaire de decision que l'arbitrage des liaisons de l'import Excel :
 * l'utilisateur l'apprend une fois. La colonne **« Règle appliquée »** est ce qui
 * fait cliquer « Valider » plutot qu'hesiter : expliquer *pourquoi* un
 * rapprochement est propose vaut mieux que le proposer sans justification.
 *
 * Les lignes ambigues (plusieurs fiches visees) sont **epinglees en haut** et
 * exclues de la validation groupee.
 */
export const ImageLinkApproxMatches = ({
  table,
  items,
  files,
  decisions,
  onDecision,
  onPositionChange,
  onAcceptAll,
}: {
  table: ImportTable;
  items: ImageLinkItem[];
  files: File[];
  /** nom de fichier -> retenu ou non */
  decisions: Map<string, { accepted: boolean; recordId?: string }>;
  onDecision: (fileName: string, accepted: boolean, recordId?: string) => void;
  onPositionChange: (fileName: string, position: string) => void;
  onAcceptAll: () => void;
}) => {
  const vt = useVisibleT();
  const { t } = useTranslation();

  const fileByName = useMemo(
    () => new Map(files.map((file) => [file.name, file])),
    [files],
  );

  /**
   * Miniatures locales.
   *
   * Les URL objet sont **revoquees au demontage** : laisser fuir 400 blob URLs
   * est un vrai probleme de memoire sur un lot de cette taille.
   */
  const urlsRef = useRef<string[]>([]);
  const thumbnails = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) {
      const file = fileByName.get(item.fileName);
      if (!file || !/^image\/(jpeg|png|webp|gif|avif)$/.test(file.type)) continue;
      const url = URL.createObjectURL(file);
      urlsRef.current.push(url);
      map.set(item.fileName, url);
    }
    return map;
  }, [items, fileByName]);

  useEffect(
    () => () => {
      for (const url of urlsRef.current) URL.revokeObjectURL(url);
      urlsRef.current = [];
    },
    [],
  );

  // Ambigus d'abord : ce sont eux qui exigent un vrai choix.
  const sorted = useMemo(
    () =>
      [...items].sort((left, right) => {
        const rank = (item: ImageLinkItem) => (item.state === "ambiguous" ? 0 : 1);
        return rank(left) - rank(right);
      }),
    [items],
  );

  const positions = optionsPosition[table as keyof typeof optionsPosition] ?? [];
  const ambiguousCount = items.filter((item) => item.state === "ambiguous").length;
  const acceptableCount = items.length - ambiguousCount;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600">
          {items.length}{" "}
          {vt(
            "bo.imageLink.approx.intro",
            "images demandent votre validation. La règle appliquée est indiquée pour chacune.",
          )}
        </p>
        {acceptableCount > 0 && (
          <Button size="sm" variant="outline" onClick={onAcceptAll}>
            {vt("bo.imageLink.approx.acceptAll", "Valider les")} {acceptableCount}{" "}
            {vt("bo.imageLink.approx.acceptAllSuffix", "correspondances approximatives")}
          </Button>
        )}
      </div>

      {ambiguousCount > 0 && (
        <p className="flex items-center gap-1.5 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4" aria-hidden />
          {ambiguousCount}{" "}
          {vt(
            "bo.imageLink.approx.ambiguousExcluded",
            "image(s) visent plusieurs fiches : elles ne sont pas concernées par la validation groupée.",
          )}
        </p>
      )}

      <div className="max-h-[22rem] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[8%]">
                {vt("bo.imageLink.approx.preview", "Aperçu")}
              </TableHead>
              <TableHead className="w-[24%]">
                {vt("bo.imageLink.approx.file", "Fichier")}
              </TableHead>
              <TableHead className="w-[22%]">
                {vt("bo.imageLink.approx.record", "Fiche proposée")}
              </TableHead>
              <TableHead className="w-[20%]">
                {vt("bo.imageLink.approx.rule", "Règle appliquée")}
              </TableHead>
              <TableHead className="w-[16%]">
                {vt("bo.imageLink.approx.position", "Position")}
              </TableHead>
              <TableHead className="w-[10%]">
                {vt("bo.imageLink.approx.decision", "Décision")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((item) => {
              const decision = decisions.get(item.fileName);
              const thumbnail = thumbnails.get(item.fileName);
              const isAmbiguous = item.state === "ambiguous";

              return (
                <TableRow key={item.fileName}>
                  <TableCell className="align-top">
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt=""
                        className="h-12 w-12 object-cover rounded"
                      />
                    ) : (
                      <ImageIcon
                        className="h-12 w-12 text-gray-300"
                        aria-hidden
                      />
                    )}
                  </TableCell>

                  <TableCell className="align-top font-mono text-xs break-all">
                    {item.fileName}
                  </TableCell>

                  <TableCell className="align-top text-sm">
                    {isAmbiguous ? (
                      <Select
                        value={decision?.recordId ?? ""}
                        onValueChange={(value) =>
                          onDecision(item.fileName, true, value)
                        }
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue
                            placeholder={vt(
                              "bo.imageLink.approx.chooseRecord",
                              "Choisir une fiche…",
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {item.candidates.map((candidate) => (
                            <SelectItem key={candidate.id} value={candidate.id}>
                              {candidate.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="font-medium">{item.recordLabel}</span>
                    )}
                  </TableCell>

                  <TableCell className="align-top">
                    <div className="flex flex-wrap gap-1">
                      {item.appliedRules.map((rule) => {
                        const label = translateImageLinkRule(t, rule);
                        const key =
                          typeof rule === "string"
                            ? rule
                            : `${rule.key}:${JSON.stringify(rule.params ?? {})}`;
                        return (
                          <Badge
                            key={key}
                            variant="secondary"
                            className="font-normal text-xs"
                          >
                            {label}
                          </Badge>
                        );
                      })}
                    </div>
                  </TableCell>

                  <TableCell className="align-top">
                    <div className="flex flex-col gap-1">
                      <Select
                        value={item.position ?? ""}
                        onValueChange={(value) =>
                          onPositionChange(item.fileName, value)
                        }
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {positions.map((position) => (
                            <SelectItem key={position} value={position}>
                              {t(position)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {item.positionOccupied && (
                        <span className="flex items-center gap-1 text-xs text-amber-700">
                          <AlertTriangle className="w-3 h-3" aria-hidden />
                          {vt(
                            "bo.imageLink.approx.occupied",
                            "image déjà présente",
                          )}
                        </span>
                      )}
                    </div>
                  </TableCell>

                  <TableCell className="align-top">
                    <div className="flex flex-col gap-1">
                      <Button
                        size="sm"
                        variant={decision?.accepted ? "default" : "outline"}
                        disabled={isAmbiguous && !decision?.recordId}
                        onClick={() =>
                          onDecision(item.fileName, true, decision?.recordId)
                        }
                      >
                        {vt("bo.imageLink.approx.link", "Lier")}
                      </Button>
                      <Button
                        size="sm"
                        variant={
                          decision && !decision.accepted ? "default" : "ghost"
                        }
                        onClick={() => onDecision(item.fileName, false)}
                      >
                        {vt("bo.imageLink.approx.exclude", "Ignorer")}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
